import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GasType, addWorkingDays, isKoreanHoliday, getHolidayName } from '@bhops/shared';
import {
  infrastructureOrdersApi,
  type InfrastructureOrderDto,
  type UpsertInfrastructureOrderBody,
} from '@/api/infrastructure-orders';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Icon } from '@/components/Icon';
import { Button, Field, FormError, Input, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
// 추가수정 2차(26-07-27) 정정 — 표시/프리필 날짜는 KST 정본으로.
import { kstYmd, kstTodayYmd, toYmdInput } from '@/lib/format-date';

// Plan §E-3-3 (PDF p.20) — 그릴러 발주 입력창.
//
// Five-stage progress (each is a date input, blank = not yet done):
//   1. contactedAt        — 고객사 연락 완료
//   2. grillOrderedAt     — 그릴러 발주 완료 (= installScheduledAt 기준점)
//   3. grillDeliveredAt   — 그릴러 납품 완료
//   4. installScheduledAt — 로봇 설치 예정일 (auto-suggested as
//                          grillOrderedAt + 5 working days; user can
//                          push it later but not earlier)
//   5. installCompletedAt — 설치 완료
//
// The min-install hint shows the operator exactly when the calendar
// allows the install to land + which holidays were skipped.

interface Props {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractHumanId?: string;
  /** Existing row (when editing) or null (when creating). */
  initial: InfrastructureOrderDto | null;
}

interface DraftForm {
  gasType: '' | GasType;
  contactedAt: string;
  contactNote: string;
  grillOrderedAt: string;
  grillDeliveredAt: string;
  installScheduledAt: string;
  installCompletedAt: string;
  notes: string;
}

/**
 * `<input type="date">` 프리필은 `toYmdInput`(= 정본 `kstYmdHyphen` + `''` 폴백).
 *
 * 왜 여기 KST 환산이 필요한가(추가수정 2차 26-07-27 정정): 설치 완료일은 아래
 * "설치 완료 처리" 버튼이 `ymdToIso(todayYmd())` = **로컬(KST) 자정** = 전날
 * T15:00Z 로 저장했으므로, UTC 로 자른 프리필에는 하루 이른 날짜가 들어왔고
 * 그 상태로 저장하면(buildBody 가 폼 값을 그대로 전송) **완료일이 실제로
 * 하루 앞으로 밀려 저장**됐다. 표시가 아니라 저장 경로 결함이었다.
 * 버튼의 저장 형식은 아래에서 함께 고쳤지만, **이미 그렇게 저장된 기존 행**이
 * DB 에 남아 있으므로 프리필의 KST 환산은 계속 필요하다.
 *
 * 다른 4개 날짜(연락/발주/납품/설치예정)는 date input 이 바로 "yyyy-mm-dd" 를
 * 보내 UTC 자정으로 저장되므로 UTC/KST 결과가 같지만, 한 폼에서 필드마다 기준이
 * 다르면 다음 사람이 또 틀린다 — 전부 KST 기준으로 통일한다.
 *
 * 한국시간 통일 5차(26-08-14) — 종전엔 이 파일이 `kstYmdInput` 이라는 **지역
 * 어댑터**(`kstYmd(v)?.replace(/\./g,'-')`)를 갖고 있었고 그 주석은 "기존
 * toYmdInput 은 UTC 를 잘라 썼다"고 적혀 있었다. 5차에서 `toYmdInput` 자체가
 * KST 정본으로 옮겨졌으므로 그 문장은 **더 이상 사실이 아니고**, 두 이름이 같은
 * 뜻을 갖게 됐다. 같은 뜻의 이름이 둘이면 다음 사람이 틀린 쪽을 집으므로
 * 지역 사본을 지우고 정본 호출로 합친다(동작은 글자 하나 안 바뀐다).
 */
function dtoToDraft(dto: InfrastructureOrderDto | null): DraftForm {
  // docs/work_log v1209 — Date 객체 leak 방지(toYmdInput 은 Date 도 받는다).
  // <input type="date"> 는 빈 문자열을 안전하게 받음.
  return {
    gasType: dto?.gasType ?? '',
    contactedAt: toYmdInput(dto?.contactedAt),
    contactNote: dto?.contactNote ?? '',
    grillOrderedAt: toYmdInput(dto?.grillOrderedAt),
    grillDeliveredAt: toYmdInput(dto?.grillDeliveredAt),
    installScheduledAt: toYmdInput(dto?.installScheduledAt),
    installCompletedAt: toYmdInput(dto?.installCompletedAt),
    notes: dto?.notes ?? '',
  };
}

export function InfrastructureOrderModal({
  open,
  onClose,
  contractId,
  contractHumanId,
  initial,
}: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<DraftForm>(() => dtoToDraft(initial));
  const [error, setError] = useState<string | null>(null);
  // round5 #15a — 설치 예정일의 auto-fill 추적. 세션 내 수동 수정 전이면
  // 발주일 변경 시 양방향(앞당김 포함) 재산출하고, 수동 설정 후엔 무단으로
  // 건드리지 않는다(고객 합의 일정 보호 — 기존 '나중으로만 밀기' 가드의
  // 이유를 유지). 대신 앞당김이 가능해지면 1-click 힌트로 의도를 가시화.
  const [installAutoFilled, setInstallAutoFilled] = useState(false);
  const [earlierHint, setEarlierHint] = useState<string | null>(null);
  // round5 #9 — 설치완료일 null→set 은 예약 로봇 DEPLOYED + handoffStatus
  // INSTALLED 풀-캐스케이드의 공식 트리거. 오입력 시 회수 플로우로 되돌려야
  // 하므로 확인 다이얼로그 필수 + (서명 없는 완료) 사유를 감사 로그에 기록.
  const [cascadeConfirm, setCascadeConfirm] = useState<UpsertInfrastructureOrderBody | null>(null);
  const [cascadeReason, setCascadeReason] = useState('');

  // Reset on open / initial change so the modal isn't sticky between
  // different contracts.
  useEffect(() => {
    if (open) {
      setForm(dtoToDraft(initial));
      setError(null);
      // 기존 저장값은 합의된 일정으로 간주(수동 취급), 빈 값은 auto-fill 대상.
      setInstallAutoFilled(false);
      setEarlierHint(null);
      setCascadeConfirm(null);
      setCascadeReason('');
    }
  }, [open, initial]);

  // §E-3-2 — minimum legal install date once grillOrderedAt is set.
  const minInstall = useMemo(() => {
    if (!form.grillOrderedAt) return null;
    try {
      return addWorkingDays(form.grillOrderedAt, 5);
    } catch {
      return null;
    }
  }, [form.grillOrderedAt]);

  const installEarlierThanMin =
    !!form.installScheduledAt && !!minInstall && form.installScheduledAt < minInstall;
  const installIsHoliday = !!form.installScheduledAt && isKoreanHoliday(form.installScheduledAt);

  // 2026-06-12 — 설치 완료 캐스케이드 요약 토스트 + 영향 화면 캐시 무효화.
  // 캐스케이드는 고객사 상태(install_done 합성)·로봇 상태(DEPLOYED)·계약
  // handoff 를 바꾸므로 해당 쿼리들을 함께 무효화해야 '저장했는데 마스터/
  // 로봇 페이지에 반영이 안 보인다' 가 재발하지 않는다.
  function reportCascade(data: { cascade?: InfrastructureOrderDto['cascade'] }): void {
    const c = data.cascade;
    if (!c) return;
    qc.invalidateQueries({ queryKey: ['customer-master'] });
    qc.invalidateQueries({ queryKey: ['robots'] });
    qc.invalidateQueries({ queryKey: ['contracts'] });
    if (c.totalReserved === 0) {
      toast.error(
        '이 계약에 배정된 로봇이 없어 로봇과 고객사 상태는 바뀌지 않았습니다. 로봇을 먼저 배정한 뒤 다시 저장해 주세요.',
        '운영중으로 바뀐 로봇이 없습니다',
      );
    } else if (c.deployedCount > 0) {
      toast.success(`로봇 ${c.deployedCount}대가 운영중으로 전환되었습니다.`, '설치 완료 반영');
    }
  }

  const upsert = useMutation({
    mutationFn: (body: UpsertInfrastructureOrderBody) => infrastructureOrdersApi.upsert(body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['infrastructure-orders'] });
      qc.invalidateQueries({ queryKey: ['infrastructure-order-by-contract', contractId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'calendar'] });
      toast.success('인프라 발주 정보가 저장되었습니다.', '저장 완료');
      reportCascade(data);
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  // FB3 §11 — 설치 완료 처리/해제 액션을 (이전엔 인프라 발주 테이블 행 인라인
  // 버튼) 편집 모달로 이동. 현재 폼 값을 그대로 round-trip 하면서
  // installCompletedAt 만 (오늘 날짜 / null) 로 교체해 즉시 persist 한다.
  //   - 완료 처리 → 오늘(KST 기준) 날짜. 추가수정 2차(26-07-27) 정정 —
  //     이전엔 로컬 자정 ISO 였고, 그 값이 편집 프리필에서 UTC 로 잘려
  //     하루 밀려 재저장되는 경로가 있었다.
  //   - 해제      → undefined → 백엔드가 null 로 비운다.
  // 성공 시 폼 로컬 상태도 동기화해 화면 표시와 저장값이 일치하도록 하고,
  // 목록 + 캘린더 캐시를 무효화한다(모달은 닫지 않음 — 계속 편집 가능).
  const completeMutation = useMutation({
    mutationFn: (completedAt: string | null) => {
      const body: UpsertInfrastructureOrderBody = {
        contractId,
        gasType: form.gasType || undefined,
        contactedAt: form.contactedAt || undefined,
        contactNote: form.contactNote || undefined,
        grillOrderedAt: form.grillOrderedAt || undefined,
        grillDeliveredAt: form.grillDeliveredAt || undefined,
        installScheduledAt: form.installScheduledAt || undefined,
        installCompletedAt: completedAt ?? undefined,
        notes: form.notes || undefined,
      };
      return infrastructureOrdersApi.upsert(body);
    },
    onSuccess: (data, completedAt) => {
      // 추가수정 2차(26-07-27) 정정 — 저장 직후 폼도 프리필과 같은 KST 기준으로
      // 되돌려 놓는다. 이어서 [저장]을 눌러도 날짜가 밀리지 않는다.
      setForm((f) => ({ ...f, installCompletedAt: completedAt ? toYmdInput(completedAt) : '' }));
      qc.invalidateQueries({ queryKey: ['infrastructure-orders'] });
      qc.invalidateQueries({ queryKey: ['infrastructure-order-by-contract', contractId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'calendar'] });
      toast.success(
        completedAt ? '설치 완료로 표시했습니다.' : '설치 완료를 해제했습니다.',
        '설치 완료',
      );
      reportCascade(data);
    },
    onError: (err) => setError(extractApiError(err)),
  });

  // round5 #9 — 이번 저장으로 설치완료일이 처음 set 되는지(서버 Trigger B
  // 발동 조건과 동일: DB before null → set).
  function isFirstCompletion(completedAt: string | undefined): boolean {
    return !!completedAt && !initial?.installCompletedAt;
  }

  function buildBody(): UpsertInfrastructureOrderBody {
    return {
      contractId,
      gasType: form.gasType || undefined,
      contactedAt: form.contactedAt || undefined,
      contactNote: form.contactNote || undefined,
      grillOrderedAt: form.grillOrderedAt || undefined,
      grillDeliveredAt: form.grillDeliveredAt || undefined,
      installScheduledAt: form.installScheduledAt || undefined,
      installCompletedAt: form.installCompletedAt || undefined,
      notes: form.notes || undefined,
    };
  }

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (installEarlierThanMin) {
      setError(
        `로봇 설치일은 ${minInstall} 이후로 잡아 주세요. 그릴러 발주일부터 5영업일이 지나야 합니다.`,
      );
      return;
    }
    const body = buildBody();
    // round5 #9 — 첫 설치 완료는 로봇 운영 전환 캐스케이드를 발동하므로
    // 확인 + 사유 입력을 거친다.
    if (isFirstCompletion(body.installCompletedAt)) {
      setCascadeConfirm(body);
      return;
    }
    upsert.mutate(body);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`인프라 발주 ${contractHumanId ?? contractId}`}
      width={560}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="infra-order-form"
            disabled={upsert.isPending || installEarlierThanMin}
          >
            {upsert.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <form id="infra-order-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <Field label="가스 종류" htmlFor="io-gas">
          <Select
            id="io-gas"
            value={form.gasType}
            onChange={(e) =>
              setForm((f) => ({ ...f, gasType: e.target.value as DraftForm['gasType'] }))
            }
          >
            <option value="">선택 안함</option>
            <option value={GasType.LPG}>LPG</option>
            <option value={GasType.LNG}>LNG</option>
            <option value={GasType.UNDECIDED}>미정</option>
          </Select>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="고객 연락 완료일" htmlFor="io-contacted">
            <Input
              id="io-contacted"
              type="date"
              value={form.contactedAt}
              onChange={(e) => setForm((f) => ({ ...f, contactedAt: e.target.value }))}
            />
          </Field>
          <Field label="연락 메모 (선택)" htmlFor="io-contact-note">
            <Input
              id="io-contact-note"
              value={form.contactNote}
              onChange={(e) => setForm((f) => ({ ...f, contactNote: e.target.value }))}
              placeholder="예: 점주와 통화, 5/12 오후 가능"
            />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field
            label="그릴러 발주일"
            htmlFor="io-grill-ordered"
            hint={
              minInstall
                ? `${minInstall}부터 설치할 수 있습니다.`
                : '발주하고 5영업일이 지나면 설치할 수 있습니다.'
            }
          >
            <Input
              id="io-grill-ordered"
              type="date"
              value={form.grillOrderedAt}
              onChange={(e) => {
                const v = e.target.value;
                // §E-3-2 — auto-fill installScheduledAt with min when the
                // install is empty OR below the new min.
                // round5 #15a — 추가로, 세션 내 수동 수정 전(auto-fill 상태)
                // 이면 발주일을 과거로 고쳐도 함께 앞당긴다. 수동 설정된
                // 일정은 보존하되 앞당김 여지를 1-click 힌트로 노출.
                let newMin = '';
                try {
                  newMin = v ? addWorkingDays(v, 5) : '';
                } catch {
                  newMin = '';
                }
                const cur = form.installScheduledAt;
                const sync = !!newMin && (!cur || cur < newMin || installAutoFilled);
                setForm((f) => ({
                  ...f,
                  grillOrderedAt: v,
                  installScheduledAt: sync ? newMin : f.installScheduledAt,
                }));
                if (sync) setInstallAutoFilled(true);
                setEarlierHint(!sync && !!newMin && !!cur && newMin < cur ? newMin : null);
              }}
            />
          </Field>
          <Field label="그릴러 납품일" htmlFor="io-grill-delivered">
            <Input
              id="io-grill-delivered"
              type="date"
              value={form.grillDeliveredAt}
              onChange={(e) => setForm((f) => ({ ...f, grillDeliveredAt: e.target.value }))}
            />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field
            label="로봇 설치 예정일"
            htmlFor="io-install-scheduled"
            hint={
              installIsHoliday
                ? `공휴일 (${getHolidayName(form.installScheduledAt)}) 입니다`
                : '그릴러 발주일부터 5영업일이 지난 날로 잡아 주세요.'
            }
          >
            <Input
              id="io-install-scheduled"
              type="date"
              value={form.installScheduledAt}
              min={minInstall ?? undefined}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, installScheduledAt: v }));
                // round5 #15a — 직접 입력 = 수동 설정. 이후 발주일 변경이
                // 이 값을 무단으로 앞당기지 않는다.
                setInstallAutoFilled(false);
                setEarlierHint(null);
              }}
              style={
                installEarlierThanMin || installIsHoliday
                  ? { borderColor: 'var(--fg-warning)' }
                  : undefined
              }
            />
            {/* round5 #22 — 사업팀이 마스터 모달에 입력한 희망 도입일
                (Deal.expectedCloseAt) 참고 표기. 시트의 '희망 도입일' 컬럼이
                제거되면서 필드팀 일정 협의의 참조처가 이곳으로 이동. */}
            {initial?.contract?.deal?.expectedCloseAt && (
              <div
                className="kuun-caption"
                style={{ marginTop: 4, color: 'var(--fg-muted)' }}
                data-testid="io-expected-intro-hint"
              >
                {/* 추가수정 2차(26-07-27) 정정 — 같은 값을 고객사 상세는 이미
                    KST 로 그린다. 여기만 UTC 자르기면 화면마다 하루 어긋난다. */}
                사업팀 희망 도입일: {kstYmd(initial.contract.deal.expectedCloseAt) ?? '–'}
              </div>
            )}
            {/* round5 #15a — 발주일이 과거로 바뀌어 최소 설치 가능일이 현재
                예정일보다 앞당겨졌을 때. 합의 일정을 무단으로 당기지 않고
                1-click 으로 의도를 확인받는다. */}
            {earlierHint && (
              <div
                className="kuun-caption"
                style={{
                  marginTop: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'var(--fg-muted)',
                }}
                data-testid="io-min-earlier-hint"
              >
                <span>이제 {earlierHint}부터 설치할 수 있습니다.</span>
                <button
                  type="button"
                  onClick={() => {
                    const v = earlierHint;
                    setForm((f) => ({ ...f, installScheduledAt: v }));
                    setInstallAutoFilled(true);
                    setEarlierHint(null);
                  }}
                  style={{
                    fontSize: 11,
                    padding: '1px 8px',
                    border: '1px solid var(--brand)',
                    background: 'var(--bg-info-subtle)',
                    color: 'var(--fg-on-info-tint)',
                    borderRadius: 'var(--r-btn)',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  data-testid="io-min-earlier-apply"
                >
                  이 날짜로 바꾸기
                </button>
              </div>
            )}
          </Field>
          <Field label="설치 완료일" htmlFor="io-install-completed">
            <Input
              id="io-install-completed"
              type="date"
              value={form.installCompletedAt}
              onChange={(e) => setForm((f) => ({ ...f, installCompletedAt: e.target.value }))}
            />
          </Field>
        </div>

        {/*
         * FB3 §11 — 설치 완료 처리/해제 액션 (인프라 발주 테이블 인라인 버튼에서
         * 이동). 한 번 클릭으로 오늘 날짜 완료 처리 또는 해제하고 즉시 저장한다.
         * 위 "설치 완료일" date 입력으로 임의 날짜를 잡은 뒤 전체 폼 저장도 가능.
         */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 0 4px',
          }}
        >
          {form.installCompletedAt ? (
            <>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                {/* 추가수정 2차(26-07-27) 정정 — 폼 값은 이미 KST "yyyy-mm-dd" 다.
                    표기만 목록(InfrastructurePage)과 같은 점 구분으로 맞춘다. */}
                설치 완료: <strong>{kstYmd(form.installCompletedAt) ?? '-'}</strong>
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => completeMutation.mutate(null)}
                disabled={completeMutation.isPending || upsert.isPending}
              >
                완료 해제
              </Button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>설치 미완료</span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  // 추가수정 2차(26-07-27) 정정 — 기존 `ymdToIso(todayYmd())` 는
                  // 브라우저 로컬 자정(KST 에선 전날 T15:00Z)으로 보내서, 저장은
                  // 됐지만 편집창을 다시 열면 UTC 로 잘린 하루 이른 날짜가 프리필
                  // 되고 그대로 저장하면 완료일이 밀렸다. 아래 "설치 완료일" date
                  // input 이 이미 보내고 있는 것과 **같은 모양**(KST 기준 맨 날짜)
                  // 으로 통일한다 — 백엔드가 UTC 자정으로 저장하고 화면은 kstYmd
                  // 로 같은 날짜를 읽으므로, PC 타임존과 무관하게 왕복이 안정된다.
                  const completedAt = kstTodayYmd();
                  // round5 #9 — 첫 완료는 캐스케이드 확인을 거친다(저장 후
                  // 모달 닫힘). 이미 완료 이력이 있던 행의 재완료는 기존
                  // 인라인 저장 유지.
                  if (isFirstCompletion(completedAt)) {
                    setCascadeConfirm({ ...buildBody(), installCompletedAt: completedAt });
                  } else {
                    completeMutation.mutate(completedAt);
                  }
                }}
                disabled={completeMutation.isPending || upsert.isPending}
              >
                설치 완료 처리
              </Button>
            </>
          )}
        </div>

        {installEarlierThanMin && (
          <div
            role="alert"
            style={{
              fontSize: 12,
              color: 'var(--fg-danger)',
              padding: '6px 0',
            }}
          >
            <Icon name="alert" size={14} aria-hidden /> 설치 예정일이 너무 이릅니다. 그릴러
            발주일부터 5영업일이 지난 <code>{minInstall}</code> 이후로 잡아 주세요.
          </div>
        )}

        <Field label="메모 (선택)" htmlFor="io-notes">
          <Textarea
            id="io-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </Field>
      </form>

      {/* round5 #9 — 설치 완료 캐스케이드 확인. 첫 완료 저장 전 1회. */}
      <Modal
        open={cascadeConfirm !== null}
        onClose={() => setCascadeConfirm(null)}
        title="설치 완료 처리"
        width={460}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setCascadeConfirm(null)}>
              취소
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!cascadeConfirm) return;
                upsert.mutate({
                  ...cascadeConfirm,
                  completionReason: cascadeReason.trim() || undefined,
                });
                setCascadeConfirm(null);
              }}
              disabled={upsert.isPending}
              data-testid="io-cascade-confirm"
            >
              설치 완료 확정
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--fg-default)' }}>
          설치 완료를 저장하면 이 계약에 배정된 로봇이 <strong>운영중</strong>으로 전환되고 고객사
          상태가 <strong>설치 완료</strong>로 올라갑니다. 잘못 입력하면 회수 절차로 되돌려야 합니다.
        </div>
        <Field
          label="사유 (선택)"
          htmlFor="io-cascade-reason"
          hint="서명 전에 완료 처리한다면 사유를 남겨 주세요. 감사 로그에 기록됩니다."
        >
          <Textarea
            id="io-cascade-reason"
            rows={2}
            value={cascadeReason}
            onChange={(e) => setCascadeReason(e.target.value)}
            placeholder="예: 고객 요청으로 서명 생략, 추후 수령 예정"
          />
        </Field>
      </Modal>
    </Modal>
  );
}

// 추가수정 2차(26-07-27) 정정 — 로컬 todayYmd 제거. 오늘(KST)은 공용
// kstTodayYmd 하나만 쓴다(로컬 재구현 금지).
